/**
 * One booking, as an operator needs to see it.
 *
 * This page already held the right facts — walk verification, incidents, the
 * full status history — but presented them as a log dump of raw enums in one
 * full-width column, titled with a cuid. The facts were there and unreadable.
 *
 * The question it answers is "what happened here, and who do I chase". So the
 * header carries the trouble markers, the strip carries the money and the
 * timings, and the status history is a timeline rather than a table of
 * ARROW-separated enum names.
 */
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeCheck,
  CalendarClock,
  Copy,
  Flame,
  History,
  IndianRupee,
  LifeBuoy,
  MapPin,
  PawPrint,
  ShieldCheck,
  Store,
  TestTube,
  Timer,
  Undo2,
  Users,
  User,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import {
  DetailPanel,
  Field,
  FieldList,
  PanelEmpty,
  PanelSkeleton,
} from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { AuthImage } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/toaster';
import { ago, date_time, humanize, inr, title_case } from '@/lib/format';
import { use_booking_detail } from '@/features/bookings/hooks';
import {
  BOOKING_STATUS_LABELS,
  DINING_MODE_LABELS,
  DINING_PRICE_UNIT_LABELS,
  OPEN_BOOKING_STATUSES,
  type BookingAudit,
  type BookingDetail,
  type BookingStatus,
  type WalkIncident,
} from '@/features/bookings/types';
import { VENDOR_MODULE_TYPE_LABELS } from '@/features/vendors/types';
import { BOOKING_STATUS_TONE } from './booking-list-page';
import { cn } from '@/lib/utils';

const PAYMENT_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'destructive'> = {
  CAPTURED: 'success',
  PENDING: 'warning',
  REFUNDED: 'destructive',
  FAILED: 'destructive',
};

const SEVERITY_TONE: Record<string, 'destructive' | 'warning' | 'neutral'> = {
  URGENT: 'destructive',
  HIGH: 'destructive',
  MEDIUM: 'warning',
  LOW: 'neutral',
};

/** Who moved the booking, said as a role rather than a table name. */
const ACTOR_LABELS: Record<string, string> = {
  USER: 'Customer',
  VENDOR: 'Vendor',
  ADMIN: 'Admin',
  SYSTEM: 'System',
};

/** Is the slot behind us with the booking still open? Mirrors the server rule. */
function is_overdue(b: BookingDetail): boolean {
  return (
    new Date(b.slot_end).getTime() < Date.now() &&
    OPEN_BOOKING_STATUSES.includes(b.status as BookingStatus)
  );
}

function overdue_label(slot_end: string): string {
  const hours = Math.floor((Date.now() - new Date(slot_end).getTime()) / 3_600_000);
  if (hours < 1) return 'ended less than an hour ago';
  if (hours < 24) return `${hours} hours overdue`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} overdue`;
}

/** Slot length, which is what "a 30-minute walk" actually means on the record. */
function slot_minutes(b: BookingDetail): number {
  return Math.max(
    0,
    Math.round((new Date(b.slot_end).getTime() - new Date(b.slot_start).getTime()) / 60_000),
  );
}

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: b, isLoading, error } = use_booking_detail(id);

  const overdue = b ? is_overdue(b) : false;
  const open_incidents = b?.incidents.filter((i) => i.status === 'OPEN') ?? [];

  const metrics: Metric[] = useMemo(
    () => [
      {
        key: 'price',
        label: 'Price',
        value: b ? inr(b.price_inr) : undefined,
        hint: b?.payment_status ? title_case(b.payment_status) : 'No payment record',
        // Money taken on a booking nobody finished is the case that needs a
        // human, so the tile says so rather than showing a neutral amount.
        tone: b?.payment_status === 'REFUNDED' ? 'destructive' : 'default',
        icon: IndianRupee,
        loading: isLoading,
      },
      {
        key: 'duration',
        label: 'Slot length',
        value: b ? `${slot_minutes(b)} min` : undefined,
        hint: b ? date_time(b.slot_start) : undefined,
        icon: Timer,
        loading: isLoading,
      },
      {
        key: 'overdue',
        label: 'Closed out',
        value: b ? (overdue ? 'No' : 'Yes') : undefined,
        hint: b && overdue ? overdue_label(b.slot_end) : 'Nothing outstanding',
        tone: overdue ? 'warning' : 'success',
        icon: CalendarClock,
        loading: isLoading,
      },
      {
        key: 'incidents',
        label: 'Incidents',
        value: b?.incidents.length ?? 0,
        hint: open_incidents.length > 0 ? `${open_incidents.length} still open` : undefined,
        tone: open_incidents.length > 0 ? 'destructive' : 'default',
        icon: AlertTriangle,
        loading: isLoading,
      },
      {
        key: 'changes',
        label: 'Status changes',
        value: b?.audits.length ?? 0,
        hint: 'How much this booking has moved',
        icon: History,
        loading: isLoading,
      },
    ],
    [b, isLoading, overdue, open_incidents.length],
  );

  return (
    <DashboardLayout
      title={b?.service_name ?? 'Booking'}
      subtitle={
        b
          ? `${VENDOR_MODULE_TYPE_LABELS[b.module_type] ?? b.module_type} · ${date_time(b.slot_start)}`
          : undefined
      }
      breadcrumbs={[{ label: b?.service_name ?? 'Booking' }]}
      avatar={
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-panel bg-muted text-muted-foreground">
          <CalendarClock aria-hidden="true" className="h-5 w-5" />
        </span>
      }
      title_badges={
        b ? (
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge
              tone={BOOKING_STATUS_TONE[b.status]}
              size="sm"
              dot
              data-testid={`booking-status-${b.status}`}
            >
              {BOOKING_STATUS_LABELS[b.status]}
            </Badge>
            {overdue && (
              <Badge tone="warning" size="sm" dot data-testid="badge-overdue">
                {overdue_label(b.slot_end)}
              </Badge>
            )}
            {open_incidents.length > 0 && (
              <Badge tone="destructive" size="sm" dot data-testid="badge-open-incident">
                {open_incidents.length} open incident{open_incidents.length === 1 ? '' : 's'}
              </Badge>
            )}
            {b.support_request_count > 0 && (
              <Badge tone="warning" size="sm">
                {b.support_request_count} support request
                {b.support_request_count === 1 ? '' : 's'}
              </Badge>
            )}
            {b.walk_plan_id && (
              <Badge tone="info" size="sm" data-testid="walk-plan-badge">
                Part of a walk plan
              </Badge>
            )}
            {b.training_package_id && (
              <Badge tone="info" size="sm" data-testid="training-package-badge">
                Part of a course
              </Badge>
            )}
          </span>
        ) : undefined
      }
      meta={
        b ? (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Booked {ago(b.created_at)}</span>
            <span>Last moved {ago(b.updated_at)}</span>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(b.id);
                notify.success('Booking ID copied');
              }}
              className="inline-flex items-center gap-1 underline-offset-2 hover:text-foreground hover:underline"
              data-testid="copy-booking-id"
            >
              <Copy aria-hidden="true" className="h-3 w-3" /> Copy ID
            </button>
          </span>
        ) : undefined
      }
      actions={
        b ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/bookings" data-testid="back-to-bookings">
                Back to bookings
              </Link>
            </Button>
            {/*
              Read-only on purpose. Cancelling and refunding a booking belong to
              the support-request flow, which records who asked, what was agreed
              and what was paid back — an unattributed refund button here would
              route round all of that.
            */}
            {b.support_request_count > 0 && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/support-requests?q=${b.id}`} data-testid="open-support">
                  <LifeBuoy className="h-4 w-4" /> Support requests
                </Link>
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link to={`/vendors/${b.vendor_profile_id}`} data-testid="open-vendor">
                <Store className="h-4 w-4" /> Vendor
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/users/${b.user_id}`} data-testid="open-customer">
                <User className="h-4 w-4" /> Customer
              </Link>
            </Button>
          </div>
        ) : undefined
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      {error ? (
        <p className="text-data text-destructive" data-testid="booking-detail-error">
          Could not load this booking.
        </p>
      ) : isLoading || !b ? (
        <PanelSkeleton rows={8} />
      ) : (
        <div className="flex flex-col gap-gutter" data-testid="booking-detail">
          {/* The one thing that should stop an operator reading further. */}
          {overdue && (
            <div
              className="flex items-start gap-2 rounded-panel bg-warning/10 px-3 py-2.5 text-data text-warning"
              data-testid="overdue-banner"
            >
              <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold">This booking was never closed out.</strong> The
                slot ended {overdue_label(b.slot_end).replace(' overdue', ' ago')} and it is still{' '}
                {BOOKING_STATUS_LABELS[b.status].toLowerCase()} — so the vendor was never paid out
                for it and the customer was never refunded or marked as a no-show.
              </span>
            </div>
          )}

          {b.status === 'CANCELLED' && (
            <div
              className="flex items-start gap-2 rounded-panel bg-muted px-3 py-2.5 text-data text-muted-foreground"
              data-testid="cancelled-banner"
            >
              <Undo2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold text-foreground">Cancelled</strong>
                {b.cancelled_by
                  ? ` by the ${(ACTOR_LABELS[b.cancelled_by] ?? b.cancelled_by).toLowerCase()}`
                  : ''}
                {b.cancel_reason ? `: “${b.cancel_reason}”` : ' — no reason was recorded.'}
              </span>
            </div>
          )}

          <div className="grid items-start gap-gutter lg:grid-cols-2">
            <DetailPanel title="What was booked" icon={PawPrint} test_id="booking-what">
              <FieldList>
                <Field label="Service" value={b.service_name} />
                <Field
                  label="Module"
                  value={VENDOR_MODULE_TYPE_LABELS[b.module_type] ?? b.module_type}
                />
                {/*
                  Only when a pet is even the point. A dining booking is a party
                  of people, so "No pet on this booking" is noise on every one of
                  the seventeen of them.
                */}
                {(b.pet || b.party_size === null) && (
                  <Field
                    label="Pet"
                    value={
                      b.pet ? (
                        <Link
                          to={`/users/${b.user_id}/pets/${b.pet.id}`}
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          {b.pet.name} · {title_case(b.pet.species)}
                        </Link>
                      ) : null
                    }
                    empty="No pet on this booking"
                  />
                )}
                <Field
                  label="Slot"
                  value={`${date_time(b.slot_start)} – ${new Date(b.slot_end).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} (${slot_minutes(b)} min)`}
                />
                <Field
                  label="Price"
                  mono
                  value={
                    <span className="flex flex-wrap items-center gap-2">
                      {inr(b.price_inr)}
                      {b.payment_status ? (
                        <Badge tone={PAYMENT_TONE[b.payment_status] ?? 'neutral'} size="sm" dot>
                          {title_case(b.payment_status)}
                        </Badge>
                      ) : (
                        <Badge tone="neutral" size="sm">
                          No payment record
                        </Badge>
                      )}
                    </span>
                  }
                />
                <Field label="Booked" value={date_time(b.created_at)} />
                <Field label="Notes from the customer" value={b.notes} empty="None left" />
              </FieldList>
            </DetailPanel>

            <div className="flex flex-col gap-gutter">
              <DetailPanel title="Who" icon={User} test_id="booking-who">
                <div className="flex flex-col gap-3">
                  <PartyRow
                    label="Customer"
                    name={b.user_name}
                    sub={b.user_phone}
                    to={`/users/${b.user_id}`}
                    seed={b.user_id}
                  />
                  <PartyRow
                    label="Vendor"
                    name={b.vendor_business_name}
                    sub={VENDOR_MODULE_TYPE_LABELS[b.module_type] ?? b.module_type}
                    to={`/vendors/${b.vendor_profile_id}`}
                    seed={b.vendor_profile_id}
                    icon={Store}
                  />
                </div>
              </DetailPanel>

              {b.pickup && (
                <DetailPanel
                  title="Where"
                  icon={MapPin}
                  description="Where the walker or trainer was asked to turn up"
                  test_id="booking-pickup"
                >
                  <FieldList>
                    <Field label="Address" value={b.pickup.address} />
                    <Field label="Landmark" value={b.pickup.landmark} empty="None given" />
                    <Field label="Pincode" value={b.pickup.pincode} mono empty="Not given" />
                    <Field
                      label="Contact on the day"
                      value={b.pickup.contact_phone}
                      mono
                      empty="Uses the account number"
                    />
                    <Field label="Access notes" value={b.pickup.access_notes} empty="None given" />
                  </FieldList>
                </DetailPanel>
              )}
            </div>
          </div>

          {/*
            A dining booking is a party of people at a table, not a pet at an
            appointment. Its facts lived as one "Party size: 2 people" row, which
            said nothing about what was sold, how long the table is held, or why
            a party of six cost six times a party of one.
          */}
          {(b.dining_offering || b.party_size !== null) && <DiningPanel booking={b} />}

          {/* WALKER bookings turn on the OTP handshake, so it gets its own panel. */}
          {(b.module_type === 'WALKER' || b.walk_otp.start_verified_at) && (
            <DetailPanel
              title="Session verification"
              icon={ShieldCheck}
              description="The OTP handshake that proves a walk actually started and ended"
              test_id="booking-verification"
            >
              <FieldList>
                <Field
                  label="Start"
                  value={
                    b.walk_otp.start_verified_at ? (
                      <span className="inline-flex items-center gap-1.5 text-success">
                        <BadgeCheck className="h-3.5 w-3.5" /> Verified{' '}
                        {date_time(b.walk_otp.start_verified_at)}
                      </span>
                    ) : null
                  }
                  empty="Never verified"
                />
                <Field
                  label="End"
                  value={
                    b.walk_otp.end_verified_at ? (
                      <span className="inline-flex items-center gap-1.5 text-success">
                        <BadgeCheck className="h-3.5 w-3.5" /> Verified{' '}
                        {date_time(b.walk_otp.end_verified_at)}
                      </span>
                    ) : b.walk_otp.end_bypassed ? (
                      // Worth flagging rather than styling as success: the walker
                      // closed the session without the owner present.
                      <span className="text-warning">
                        Bypassed — owner unavailable
                        {b.walk_otp.end_bypass_reason ? `: “${b.walk_otp.end_bypass_reason}”` : ''}
                      </span>
                    ) : null
                  }
                  empty="Never verified"
                />
              </FieldList>
            </DetailPanel>
          )}

          {b.incidents.length > 0 && <IncidentsPanel incidents={b.incidents} />}

          {b.deceased_pet && (
            <DetailPanel title="In memory of" icon={Flame} test_id="booking-deceased-pet">
              <FieldList>
                <Field label="Name" value={b.deceased_pet.name} empty="Not given" />
                <Field
                  label="Species"
                  value={b.deceased_pet.species ? title_case(b.deceased_pet.species) : null}
                  empty="Not given"
                />
                <Field
                  label="Weight"
                  value={b.deceased_pet.weight_kg ? `${b.deceased_pet.weight_kg} kg` : null}
                  empty="Not given"
                />
              </FieldList>
            </DetailPanel>
          )}

          {b.cremation && (
            <DetailPanel
              title="Cremation journey"
              icon={Flame}
              description={`Currently ${humanize(b.cremation.status).toLowerCase()}`}
              test_id="booking-cremation-journey"
            >
              <Journey
                steps={[
                  ['Pickup confirmed', b.cremation.confirmed_pickup_at],
                  ['Collected', b.cremation.picked_up_at],
                  ['Cremation started', b.cremation.cremation_started_at],
                  ['Ashes ready', b.cremation.ashes_ready_at],
                  ['Ashes returned', b.cremation.ashes_returned_at],
                ]}
              />
              {b.cremation.certificate_number && (
                <FieldList className="mt-3">
                  <Field label="Certificate" value={b.cremation.certificate_number} mono />
                  <Field
                    label="Cremated"
                    value={b.cremation.cremated_at ? date_time(b.cremation.cremated_at) : null}
                    empty="Not recorded"
                  />
                </FieldList>
              )}
            </DetailPanel>
          )}

          {b.lab && (
            <DetailPanel
              title="Lab journey"
              icon={TestTube}
              description={`Currently ${humanize(b.lab.status).toLowerCase()}`}
              test_id="booking-lab-journey"
            >
              <Journey
                steps={[
                  ['Confirmed', b.lab.confirmed_at],
                  ['Sample collected', b.lab.sample_collected_at],
                  ['Processing', b.lab.processing_at],
                  ['Report ready', b.lab.report_ready_at],
                  ['Reported to the owner', b.lab.reported_at],
                ]}
              />
              {b.lab.report_notes && (
                <p className="mt-3 rounded-panel bg-muted/50 p-2.5 text-data">
                  {b.lab.report_notes}
                </p>
              )}
              {b.lab.report_documents.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {b.lab.report_documents.map((doc) => (
                    <AuthImage
                      key={doc.id}
                      path={doc.thumb_path}
                      alt=""
                      className="h-24 w-24 rounded-panel border border-border object-cover"
                    />
                  ))}
                </div>
              )}
            </DetailPanel>
          )}

          <HistoryPanel audits={b.audits} />
        </div>
      )}
    </DashboardLayout>
  );
}

/**
 * What a café or restaurant booking actually is.
 *
 * The party size against the offering's own limits, the table turn, and the
 * pricing arithmetic — because `PER_GUEST` means the total is the base times the
 * party, and an operator checking a refund needs to see that rather than infer
 * it.
 */
function DiningPanel({ booking }: { booking: BookingDetail }) {
  const o = booking.dining_offering;
  const party = booking.party_size;
  const per_guest = o?.price_unit === 'PER_GUEST' && party && party > 0;

  return (
    <DetailPanel
      title={o ? DINING_MODE_LABELS[o.mode] : 'Dining booking'}
      icon={Users}
      description={
        o
          ? `Priced ${DINING_PRICE_UNIT_LABELS[o.price_unit]}, ${o.capacity} covers in the room`
          : // Ten of the seventeen dining bookings have an offering; the rest are
            // older rows whose offering was deleted, and saying so beats a blank.
            'The offering behind this booking has since been removed'
      }
      test_id="booking-dining"
    >
      <FieldList>
        <Field
          label="Party"
          value={
            party === null ? null : (
              <span className="flex flex-wrap items-center gap-2">
                {party} {party === 1 ? 'guest' : 'guests'}
                {o && (party < o.min_party || party > o.max_party) && (
                  // Worth flagging: the booking sits outside what the vendor said
                  // they would take, which is how an over-booked table happens.
                  <Badge tone="warning" size="sm" dot>
                    outside the {o.min_party}–{o.max_party} they accept
                  </Badge>
                )}
              </span>
            )
          }
          empty="Not recorded"
        />
        {o && (
          <Field
            label="Table held for"
            value={o.turn_minutes ? `${o.turn_minutes} minutes` : null}
            empty="No turn time set"
          />
        )}
        <Field
          label="Price"
          mono
          value={
            per_guest && o ? (
              <span>
                {inr(booking.price_inr)}{' '}
                <span className="text-muted-foreground">
                  ({inr(Number(booking.price_inr) / party!)} × {party} guests)
                </span>
              </span>
            ) : (
              inr(booking.price_inr)
            )
          }
        />
        {o && <Field label="Room capacity" value={`${o.capacity} covers`} />}
      </FieldList>
    </DetailPanel>
  );
}

function PartyRow({
  label,
  name,
  sub,
  to,
  seed,
  icon: Icon,
}: {
  label: string;
  name: string | null;
  sub: string | null;
  to: string;
  seed: string;
  icon?: typeof Store;
}) {
  return (
    <div className="flex items-center gap-3">
      {Icon ? (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-muted text-muted-foreground">
          <Icon aria-hidden="true" className="h-4 w-4" />
        </span>
      ) : (
        <PersonAvatar path={null} name={name} seed={seed} size="md" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-micro font-bold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="truncate text-data font-semibold">{name ?? 'No name on file'}</p>
        {sub && <p className="truncate text-micro text-muted-foreground">{sub}</p>}
      </div>
      <Button variant="ghost" size="sm" asChild>
        <Link to={to}>Open</Link>
      </Button>
    </div>
  );
}

/**
 * A fulfilment journey as a set of stamps rather than a wall of nullable dates.
 *
 * The steps a vendor has not reached yet are dimmed instead of hidden, so an
 * operator can see where in the process something stalled.
 */
function Journey({ steps }: { steps: [string, string | null][] }) {
  return (
    <ol className="flex flex-col gap-0">
      {steps.map(([label, at], i) => (
        <li key={label} className="flex items-start gap-2.5">
          <span className="flex flex-col items-center self-stretch">
            <span
              className={cn(
                'mt-1 h-2.5 w-2.5 shrink-0 rounded-full',
                at ? 'bg-success' : 'bg-border',
              )}
            />
            {i < steps.length - 1 && <span className="w-px flex-1 bg-border" />}
          </span>
          <span className={cn('pb-3', !at && 'text-muted-foreground/60')}>
            <span className="block text-data font-medium">{label}</span>
            <span className="block text-micro text-muted-foreground">
              {at ? date_time(at) : 'Not yet'}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function IncidentsPanel({ incidents }: { incidents: WalkIncident[] }) {
  const open = incidents.filter((i) => i.status === 'OPEN').length;
  return (
    <DetailPanel
      title="Incidents"
      icon={AlertTriangle}
      description={
        open > 0
          ? `${open} of ${incidents.length} still open`
          : `${incidents.length} reported, all resolved`
      }
      test_id="booking-incidents"
    >
      <ul className="flex flex-col divide-y divide-border/60">
        {incidents.map((i) => (
          <li key={i.id} className="flex flex-col gap-1.5 py-2.5 first:pt-0 last:pb-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-data font-semibold">{humanize(i.type)}</span>
              <Badge tone={SEVERITY_TONE[i.severity] ?? 'neutral'} size="sm" dot>
                {title_case(i.severity)}
              </Badge>
              <Badge tone={i.status === 'OPEN' ? 'warning' : 'success'} size="sm">
                {title_case(i.status)}
              </Badge>
              <span className="text-micro text-muted-foreground">{date_time(i.created_at)}</span>
            </span>
            <p className="text-data">{i.note}</p>
            {i.resolution_note && (
              <p className="text-data text-muted-foreground">
                Resolved: {i.resolution_note}
                {i.resolved_at ? ` · ${date_time(i.resolved_at)}` : ''}
              </p>
            )}
            <p className="text-micro text-muted-foreground">
              {i.owner_acknowledged_at
                ? `Owner acknowledged ${date_time(i.owner_acknowledged_at)}`
                : // The owner not having seen it is the part that generates a call.
                  'Owner has not acknowledged this yet'}
            </p>
            {i.photos.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {i.photos.map((photo) => (
                  <AuthImage
                    key={photo.id}
                    path={photo.thumb_path}
                    alt=""
                    className="h-20 w-20 rounded-panel border border-border object-cover"
                  />
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </DetailPanel>
  );
}

/**
 * Every status change, as a sentence.
 *
 * This was a table of `PENDING_PAYMENT → AWAITING_VENDOR` with a `USER` chip
 * beside it. The facts were right and it read like a database log; the whole
 * value of this panel is being able to skim who did what, in order.
 */
function HistoryPanel({ audits }: { audits: BookingAudit[] }) {
  return (
    <DetailPanel
      title="History"
      icon={History}
      description={`${audits.length} status change${audits.length === 1 ? '' : 's'}`}
      test_id="booking-history"
    >
      {audits.length === 0 ? (
        <PanelEmpty icon={History}>Nothing has moved this booking since it was created.</PanelEmpty>
      ) : (
        <ol className="flex flex-col divide-y divide-border/60">
          {audits.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-2 py-2 first:pt-0 last:pb-0">
              <Badge tone={BOOKING_STATUS_TONE[a.to_status] ?? 'neutral'} size="sm" dot>
                {BOOKING_STATUS_LABELS[a.to_status] ?? humanize(a.to_status)}
              </Badge>
              <span className="text-data">
                {a.from_status ? (
                  <>
                    from{' '}
                    <span className="text-muted-foreground">
                      {BOOKING_STATUS_LABELS[a.from_status] ?? humanize(a.from_status)}
                    </span>
                  </>
                ) : (
                  'created'
                )}
                {' · '}
                <span className="font-medium">{ACTOR_LABELS[a.actor_type] ?? a.actor_type}</span>
              </span>
              <span className="text-micro text-muted-foreground">{date_time(a.created_at)}</span>
              {a.reason && (
                <span className="w-full text-data text-muted-foreground">“{a.reason}”</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </DetailPanel>
  );
}

export default BookingDetailPage;
